/**
 * Co powiedzieć zaproszonemu, któremu `accept_invitation` odmówiło.
 *
 * RPC jest SECURITY DEFINER i rozstrzyga wszystko sam: czy zaproszenie żyje, czy jest na adres
 * zalogowanego konta (migracja 793 — adres z `auth.users`, nie z `public.users.email`), czy
 * organizacja ma wolne konto w pakiecie (bramka puli kont, 671 — wyzwalacz na
 * `user_organizations` rzuca `user_limit_exceeded` wewnątrz RPC) i czy człowiek już jest członkiem.
 * Ekran tłumaczy odmowę na zdanie z katalogu języka ekranu. `hint` serwera na ekran nie trafia,
 * bo jest po polsku niezależnie od języka ekranu.
 *
 * Świadomie przyjęta utrata: 671 rzuca `user_limit_exceeded` w DWÓCH gałęziach (pula wyczerpana,
 * „…(5/5)”, i konta panelu wyłączone w pakiecie), a rozróżnia je wyłącznie `hint`. Dlatego
 * `invitation.userLimit` mówi to, co jest prawdą w obu: organizacja nie ma w pakiecie wolnego
 * konta, pomoże zmiana pakietu albo zwolnienie miejsca. Liczba kont zostaje w `hint` odpowiedzi
 * RPC (konsola, sieć) dla tego, kto przyjmie zgłoszenie.
 *
 * Do 16.10.2026 ekran po każdej odmowie RPC próbował bezpośredniego `INSERT` do
 * `user_organizations` jako zaproszony. Polityka `user_orgs_insert_self` (115) taki zapis
 * przewiduje, ale na produkcji kończy się on 42P17 „infinite recursion detected in policy”
 * (zmierzone w 793): jej `WITH CHECK` czyta `organization_invitations`, której polityka czyta
 * z powrotem `user_organizations`. PostgreSQL wykrywa tę rekurencję przy przepisywaniu zapytania,
 * PRZED wyzwalaczami BEFORE — wyzwalacz 671 przy tym zapisie nie odpalał, a zaproszony widział
 * odmowę zapasowej drogi zamiast powodu z RPC: „limit kont” i „to zaproszenie jest na inny adres”
 * nie dochodziły do niego nigdy. Drogi zapasowej nie ma też z drugiego powodu: po naprawie
 * rekurencji ta polityka przepuściłaby zapis członkostwa z ekranu obok RPC, czyli drugą drogę do
 * tego samego stanu, rozstrzyganą innymi warunkami niż funkcja.
 *
 * Fragmenty z `ROZPOZNANE` są powiązane z SQL bramką
 * `tests/unit/supabase/odmowyPrzyjeciaZaproszenia.test.ts`: każdy musi stać w żywej definicji
 * funkcji, która go rzuca, i każdy wyjątek `accept_invitation` musi mieć tu swój wpis.
 */
export type StanOdmowy = 'success' | 'not_found' | 'no_session' | 'error';

export interface OdmowaZaproszenia {
  status: StanOdmowy;
  /** Klucz `invitation.*` w katalogu języka. */
  klucz: string;
  /** Wartość zastępcza dla `t()` — ta sama, co w `pl.json` (jak w pozostałych wywołaniach ekranu). */
  zastepcze: string;
}

/**
 * Zdania wyjątków `accept_invitation` (793) i bramki puli kont (671), dokładnie jak w SQL —
 * pierwszy pasujący fragment wygrywa.
 */
export const ROZPOZNANE: ReadonlyArray<readonly [fragment: string, odmowa: OdmowaZaproszenia]> = [
  [
    'user_limit_exceeded',
    {
      status: 'error',
      klucz: 'invitation.userLimit',
      zastepcze:
        'Ta organizacja nie ma w swoim pakiecie wolnego konta dla nowej osoby. Poproś administratora o zmianę pakietu albo o zwolnienie miejsca.',
    },
  ],
  [
    'This invitation is not for your email address',
    {
      status: 'error',
      klucz: 'invitation.wrongEmail',
      zastepcze:
        'To zaproszenie wysłano na inny adres e-mail. Zaloguj się kontem, na które przyszło zaproszenie, albo poproś administratora o zaproszenie na ten adres.',
    },
  ],
  [
    'You are already a member of this organization',
    {
      status: 'success',
      klucz: 'invitation.alreadyMember',
      zastepcze: 'Jesteś już członkiem tej organizacji',
    },
  ],
  [
    'Invalid or expired invitation',
    {
      status: 'not_found',
      klucz: 'invitation.notFound',
      zastepcze:
        'Tego zaproszenia nie ma albo straciło ważność. Poproś administratora organizacji o nowe.',
    },
  ],
  [
    'Not authenticated',
    {
      status: 'no_session',
      klucz: 'invitation.noSession',
      zastepcze:
        'Nie udało się ustalić Twojej sesji. Zaloguj się i otwórz link z zaproszenia ponownie — a jeśli link jest starszy niż godzina, poproś o nowy.',
    },
  ],
];

/** Odmowa, której zdania nie ma w `ROZPOZNANE`. */
export const NIEROZPOZNANA: OdmowaZaproszenia = {
  status: 'error',
  klucz: 'invitation.acceptError',
  zastepcze: 'Nie można zaakceptować zaproszenia',
};

export function rozpoznajOdmoweZaproszenia(blad: { message?: string | null }): OdmowaZaproszenia {
  const tresc = blad.message ?? '';
  for (const [fragment, odmowa] of ROZPOZNANE) {
    if (tresc.includes(fragment)) return odmowa;
  }
  return NIEROZPOZNANA;
}
